import type { IEngagementVideo } from '@schibsted/pulse-sdk';
import logger from 'utils/logger';

export type PlayEntry = Exclude<
    IEngagementVideo['interaction'],
    undefined
>['playEntry'];

// keyed by the SDK type: tsc fails here when the SDK adds or removes a value
const PLAY_ENTRIES: Record<PlayEntry, true> = {
    Direct: true,
    Next: true,
    Previous: true,
    Replay: true,
    Other: true,
};

// playbackSource values set by the player (see playback-events.ts) mapped to
// the closest playEntry. Sources may carry a "-<origin>" suffix, e.g. "manualNext-grid".
const PLAYBACK_SOURCE_ENTRIES = new Map<string, PlayEntry>([
    ['autoplayNext', 'Next'],
    ['manualNext', 'Direct'],
    ['replay', 'Replay'],
]);

function mapPlaybackSource(playbackSource: string): string {
    const base = playbackSource.split('-')[0] ?? playbackSource;
    return PLAYBACK_SOURCE_ENTRIES.get(base) ?? playbackSource;
}

function isPlayEntry(value: string): value is PlayEntry {
    return Object.prototype.hasOwnProperty.call(PLAY_ENTRIES, value);
}

/**
 * Resolves the TPaaS `playEntry` from an externally-set value or the
 * `playbackSource` (mapped to the closest entry), defaulting to "Direct". Anything the tracker does not
 * accept is reported as "Other" and logged as an error so a developer can
 * notice and fix the caller.
 */
export function resolvePlayEntry(
    override: string | undefined,
    playbackSource: string | undefined,
): PlayEntry {
    const value =
        override ??
        (playbackSource ? mapPlaybackSource(playbackSource) : 'Direct');
    if (isPlayEntry(value)) {
        return value;
    }
    logger('Tpaas').error(
        `Invalid playEntry "${value}", using "Other". Allowed values: ${Object.keys(PLAY_ENTRIES).join(', ')}.`,
    );
    return 'Other';
}
